/**
 * User Preferences Sync Service
 *
 * Preferences are stored as two documents, not one, because they were never
 * one thing:
 *
 *   <scope>/userPreferences/current   — the household's cooking profile
 *   users/{uid}/userPreferences/personal — this person's own settings
 *
 * Everything about *what the household eats* — dietary restrictions, disliked
 * ingredients, cook-time limits, the meal schedule, family size — is shared
 * when paired, and always was. But a handful of fields are about the person
 * sitting in front of the app, and sharing those was a bug wearing the costume
 * of a feature. `dailyDigestOptIn` is the clearest: api/cron/runner.ts sends a
 * separate email to every member and reads one shared flag to decide, so a
 * partner switching off their nightly digest switched off yours too. The code
 * there says as much — "the toggle is shared across the household in V1".
 *
 * Splitting them also dissolves most of the merge problem. There is no
 * defensible way to merge two people's preference documents into one, but
 * there is no need to: the shared half genuinely belongs to the household, and
 * the personal half never moves.
 *
 * Migration is lazy, not scripted. The shared document keeps its exact path,
 * so existing data stays valid — reads fall back to the personal fields still
 * sitting in the shared document, and the next write moves them across and
 * deletes them from the shared copy. Nothing to backfill.
 *
 * Path: households/{hid}/userPreferences/current when paired,
 *       users/{uid}/userPreferences/current     when solo,
 *       users/{uid}/userPreferences/personal    always.
 */

import {
  doc,
  setDoc,
  getDoc,
  deleteField,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { firestore, isFirebaseEnabled } from './firebaseConfig';
import { resolveDataScope, buildScopedPath } from './householdPaths';
import type { UserPreferencesState } from '../redux/userPreferencesSlice';

/**
 * Fields that belong to a person rather than a household.
 *
 * The test is not "is this small" but "would a partner changing it be a
 * surprise?" A shared dietary restriction is the point of pairing. A shared
 * "I dismissed this banner" is a bug.
 */
export const PERSONAL_FIELDS = [
  'dailyDigestOptIn',
  'measurementSystem',
  'hasSeenMyTopRatedBanner',
  'dismissedFallbackMealIds',
] as const;

export type PersonalField = (typeof PERSONAL_FIELDS)[number];
export type PersonalPreferences = Pick<UserPreferencesState, PersonalField>;

interface FirestoreDoc {
  updatedAt: Timestamp | string;
  [key: string]: unknown;
}

const getSharedDocRef = (userId: string, householdId?: string | null) => {
  if (!firestore) {
    throw new Error('Firestore not initialized');
  }
  const scope = resolveDataScope(userId, householdId);
  if (!scope) {
    throw new Error('Cannot resolve data scope: missing userId and householdId');
  }
  const [root, id, ...rest] = buildScopedPath(scope, 'userPreferences', 'current');
  return doc(firestore, root, id, ...rest);
};

/** Always user-scoped — that is the entire point of this document. */
const getPersonalDocRef = (userId: string) => {
  if (!firestore) {
    throw new Error('Firestore not initialized');
  }
  return doc(firestore, 'users', userId, 'userPreferences', 'personal');
};

/**
 * Strip undefined fields recursively — Firestore rejects them.
 */
const cleanForFirestore = <T>(value: T): T => {
  if (Array.isArray(value)) {
    return value.map((item) => cleanForFirestore(item)) as unknown as T;
  }
  if (value !== null && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [key, v] of Object.entries(value as Record<string, unknown>)) {
      if (v === undefined) continue;
      out[key] = cleanForFirestore(v);
    }
    return out as T;
  }
  return value;
};

/** Divide a preferences object along the personal/shared line. */
export const splitPreferences = (
  preferences: UserPreferencesState
): { shared: Record<string, unknown>; personal: Record<string, unknown> } => {
  const shared: Record<string, unknown> = {};
  const personal: Record<string, unknown> = {};
  const isPersonal = new Set<string>(PERSONAL_FIELDS);

  for (const [key, value] of Object.entries(preferences)) {
    if (isPersonal.has(key)) personal[key] = value;
    else shared[key] = value;
  }
  return { shared, personal };
};

export const syncUserPreferencesToCloud = async (
  userId: string,
  preferences: UserPreferencesState,
  householdId?: string | null
): Promise<void> => {
  if (!isFirebaseEnabled() || !firestore) {
    throw new Error('Firebase not enabled');
  }

  const { shared, personal } = splitPreferences(preferences);

  // Personal fields are actively removed from the shared document, not merely
  // omitted. A stale `dailyDigestOptIn` left behind there would still be read
  // by the legacy fallback in api/cron/runner.ts — and by a partner.
  const sharedPayload: Record<string, unknown> = {
    ...cleanForFirestore(shared),
    updatedAt: serverTimestamp(),
  };
  for (const field of PERSONAL_FIELDS) sharedPayload[field] = deleteField();

  await Promise.all([
    setDoc(getSharedDocRef(userId, householdId), sharedPayload, { merge: true }),
    setDoc(
      getPersonalDocRef(userId),
      { ...cleanForFirestore(personal), updatedAt: serverTimestamp() },
      { merge: true }
    ),
  ]);
};

/**
 * The app language, for the server's emails. It lives in the personal
 * document because it belongs to the person, not the household — two partners
 * can read the app in two languages — but it is not a preferences field: the
 * language slice owns it, and this is its only writer. `fetchUserPreferencesFromCloud`
 * strips it back out, so it never lands in the preferences slice and from
 * there in the shared document.
 */
export const PERSONAL_LANGUAGE_FIELD = 'language';

export const syncPersonalLanguageToCloud = async (
  userId: string,
  language: 'en' | 'fr'
): Promise<void> => {
  if (!isFirebaseEnabled() || !firestore) {
    throw new Error('Firebase not enabled');
  }
  await setDoc(
    getPersonalDocRef(userId),
    { [PERSONAL_LANGUAGE_FIELD]: language, languageUpdatedAt: serverTimestamp() },
    { merge: true }
  );
};

export const fetchUserPreferencesFromCloud = async (
  userId: string,
  householdId?: string | null
): Promise<UserPreferencesState | null> => {
  if (!isFirebaseEnabled() || !firestore) {
    throw new Error('Firebase not enabled');
  }

  const [sharedSnap, personalSnap] = await Promise.all([
    getDoc(getSharedDocRef(userId, householdId)),
    getDoc(getPersonalDocRef(userId)),
  ]);

  if (!sharedSnap.exists() && !personalSnap.exists()) return null;

  const { updatedAt: _s, ...sharedData } = (sharedSnap.exists()
    ? sharedSnap.data()
    : {}) as FirestoreDoc;
  const {
    updatedAt: _p,
    [PERSONAL_LANGUAGE_FIELD]: _language,
    languageUpdatedAt: _l,
    ...personalData
  } = (personalSnap.exists() ? personalSnap.data() : {}) as FirestoreDoc;

  // Personal wins. When the personal document does not exist yet, whatever is
  // still in the shared document carries the user through until their next
  // write splits it — that is the lazy migration, and it is why no backfill
  // script was needed.
  return { ...sharedData, ...personalData } as unknown as UserPreferencesState;
};
